// Clientes OAuth (ChatGPT) — crear (secreto una sola vez), editar redirect URIs, rotar secreto, desactivar.
import { useState } from 'react';
import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../../components/ui/dialog';
import { Plus, Loader2, KeyRound, Link2, Ban, CheckCircle2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { copiar, CopiaRow } from './oauthUtils';

const err = (e, fallback) => toast.error(e.response?.data?.detail || fallback);
const parseUris = (txt) => txt.split('\n').map((s) => s.trim()).filter(Boolean);

export function SecretoAlert({ secreto, onCerrar }) {
  if (!secreto) return null;
  return (
    <div className="rounded-xl border border-emerald-300 bg-emerald-50 p-4" data-testid="oauth-secreto-alert">
      <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900"><CheckCircle2 className="h-4 w-4" /> Credenciales de &quot;{secreto.nombre}&quot; — copia el Client Secret AHORA, no se volverá a mostrar</div>
      <div className="mt-3 space-y-2">
        <CopiaRow label="Client ID" valor={secreto.client_id} testid="oauth-secreto-client-id" />
        <CopiaRow label="Client Secret" valor={secreto.client_secret} testid="oauth-secreto-client-secret" />
      </div>
      <div className="mt-3 flex justify-end"><Button size="sm" variant="ghost" onClick={onCerrar} data-testid="oauth-secreto-cerrar">Ya lo guardé</Button></div>
    </div>
  );
}

function ClienteDialog({ open, onOpenChange, cliente, onDone }) {
  const [nombre, setNombre] = useState(cliente?.nombre || 'ChatGPT');
  const [uris, setUris] = useState((cliente?.redirect_uris || []).join('\n'));
  const [busy, setBusy] = useState(false);
  const guardar = async () => {
    setBusy(true);
    try {
      const body = { nombre: nombre.trim() || 'ChatGPT', redirect_uris: parseUris(uris) };
      const r = cliente ? await api.put(`/oauth/admin/clientes/${cliente.client_id}`, body) : await api.post('/oauth/admin/clientes', body);
      onDone(cliente ? null : r.data);
      toast.success(cliente ? 'Cliente actualizado' : 'Cliente OAuth creado');
      onOpenChange(false);
    } catch (e) { err(e, 'No se pudo guardar el cliente'); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="oauth-cliente-dialog">
        <DialogHeader>
          <DialogTitle>{cliente ? 'Editar cliente OAuth' : 'Nuevo cliente OAuth (ChatGPT)'}</DialogTitle>
          <DialogDescription>Las URLs de retorno oficiales de chatgpt.com se aceptan siempre. Si ChatGPT te muestra una URL de retorno propia del conector, pégala aquí tal cual (una por línea).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium">Nombre</label>
            <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="ChatGPT" data-testid="oauth-cliente-nombre" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">Redirect URIs adicionales (opcional)</label>
            <Textarea value={uris} onChange={(e) => setUris(e.target.value)} rows={3} className="font-mono text-xs" placeholder="https://chatgpt.com/connector/oauth/abc123" data-testid="oauth-cliente-uris" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={guardar} disabled={busy} data-testid="oauth-cliente-guardar">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />} {cliente ? 'Guardar' : 'Crear cliente'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OAuthClientes({ clientes, onReload, onSecreto }) {
  const [dialog, setDialog] = useState({ open: false, cliente: null });
  const rotar = async (c) => {
    if (!window.confirm(`¿Rotar el Client Secret de "${c.nombre}"? Deberás actualizarlo en ChatGPT. Las conexiones ya autorizadas siguen vigentes.`)) return;
    try { const r = await api.post(`/oauth/admin/clientes/${c.client_id}/rotar-secreto`); onSecreto({ ...r.data, nombre: c.nombre }); } catch (e) { err(e, 'No se pudo rotar el secreto'); }
  };
  const desactivar = async (c) => {
    if (!window.confirm(`¿Desactivar "${c.nombre}"? Se revocarán sus ${c.conexiones_activas} conexiones activas y ChatGPT dejará de poder conectarse.`)) return;
    try { await api.delete(`/oauth/admin/clientes/${c.client_id}`); toast.success('Cliente desactivado'); onReload(); } catch (e) { err(e, 'No se pudo desactivar'); }
  };
  return (
    <div className="space-y-3" data-testid="oauth-clientes">
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">Clientes OAuth</p>
        <Button size="sm" onClick={() => setDialog({ open: true, cliente: null })} data-testid="oauth-btn-nuevo-cliente"><Plus className="mr-1 h-4 w-4" /> Nuevo cliente</Button>
      </div>
      {clientes.length === 0 ? (
        <p className="rounded-lg border border-dashed py-6 text-center text-sm text-muted-foreground" data-testid="oauth-clientes-empty">Aún no hay clientes. Crea uno para obtener el Client ID / Secret que pide ChatGPT.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Client ID</th><th className="py-2 pr-3">Redirect URIs</th><th className="py-2 pr-3">Conexiones</th><th className="py-2 pr-3">Estado</th><th className="py-2" /></tr></thead>
            <tbody>
              {clientes.map((c) => (
                <tr key={c.client_id} className="border-b last:border-0" data-testid={`oauth-cliente-row-${c.client_id}`}>
                  <td className="py-2 pr-3 font-medium">{c.nombre}<span className="ml-1 text-[10px] text-muted-foreground">{c.tipo === 'dcr' ? '(registro dinámico)' : ''}</span></td>
                  <td className="py-2 pr-3 font-mono text-xs"><span className="inline-flex items-center gap-1">{c.client_id}<button type="button" onClick={() => copiar(c.client_id, 'Client ID')} className="text-muted-foreground hover:text-foreground" data-testid={`oauth-cliente-copy-${c.client_id}`}><Copy className="h-3 w-3" /></button></span></td>
                  <td className="py-2 pr-3 text-xs">{(c.redirect_uris || []).length === 0 ? <span className="text-muted-foreground">Solo chatgpt.com (oficiales)</span> : c.redirect_uris.map((u) => <p key={u} className="truncate font-mono max-w-[260px]" title={u}>{u}</p>)}</td>
                  <td className="py-2 pr-3 text-xs">{c.conexiones_activas}</td>
                  <td className="py-2 pr-3">{c.activo ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Activo</Badge> : <Badge variant="secondary">Desactivado</Badge>}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {c.activo && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => setDialog({ open: true, cliente: c })} data-testid={`oauth-cliente-editar-${c.client_id}`}><Link2 className="mr-1 h-3.5 w-3.5" /> URIs</Button>
                        {!c.publico && <Button variant="ghost" size="sm" onClick={() => rotar(c)} data-testid={`oauth-cliente-rotar-${c.client_id}`}><KeyRound className="mr-1 h-3.5 w-3.5" /> Rotar secreto</Button>}
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => desactivar(c)} data-testid={`oauth-cliente-desactivar-${c.client_id}`}><Ban className="mr-1 h-3.5 w-3.5" /> Desactivar</Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dialog.open && <ClienteDialog open onOpenChange={(o) => setDialog({ open: o, cliente: null })} cliente={dialog.cliente} onDone={(nuevo) => { if (nuevo) onSecreto(nuevo); onReload(); }} />}
    </div>
  );
}
